import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import { generatePath, NavLink } from "react-router-dom";

import AddressDisplay from "~/components/address/AddressDisplay.tsx";
import CardValue from "~/components/Card/components/CardValue.tsx";
import { CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type {
	CentralInvoiceRecipient,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientTypes.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

type Props = {
	invoiceRecipient: CentralInvoiceRecipient
	isSelfBilledInvoice: boolean;
};

const BillingSettingsInvoiceRecipientDisplay = ({ invoiceRecipient, isSelfBilledInvoice }: Props) => {
	const {
		address,
		clientId,
		comment,
		displayName,
		invoiceByMail,
		invoiceEmail,
		legalName,
		supportContactEmail,
		supportContactPhone,
		supportContactName,
	} = invoiceRecipient;

	const contactDetailsContent = <>
		<CardValue label="Name Zentraler Rechnungsempfänger">
			<NavLink className="flex items-center gap-x-1 hover:text-secondary-500"
					 to={generatePath(CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH, {
						 clientId,
						 centralInvoiceRecipientId: invoiceRecipient.id,
					 })}>
				{displayName}
				<ArrowTopRightOnSquareIcon className="ml-1 size-4" />
			</NavLink>
		</CardValue>
		<CardValue label="Support AP">
			{supportContactName || "k/A"}
		</CardValue>
		<CardValue label="Support Telefon"
				   enableCopyToClipboard={!!(supportContactPhone)}>
			{supportContactPhone || EMPTY_VALUE_PLACEHOLDER}
		</CardValue>
		<CardValue label="Support E-Mail"
				   enableCopyToClipboard={!!(supportContactEmail)}>
			{supportContactEmail || EMPTY_VALUE_PLACEHOLDER}
		</CardValue>
	</>;

	if (isSelfBilledInvoice) {
		return <div className="grid grid-cols-2 gap-x-6 gap-y-4">
			{contactDetailsContent}
		</div>;
	}

	return <div className="grid grid-cols-2 gap-x-6">
		<div className="space-y-4">
			<CardValue label="Name Zentraler Rechnungsempfänger">
				<NavLink className="flex items-center gap-x-1 hover:text-secondary-500"
						 to={generatePath(CENTRAL_INVOICE_RECIPIENT_DETAILS_PATH, {
							 clientId,
							 centralInvoiceRecipientId: invoiceRecipient.id,
						 })}>
					{displayName}
					<ArrowTopRightOnSquareIcon className="ml-1 size-4" />
				</NavLink>
			</CardValue>
			<CardValue label="Rechnungsversand per">
				{invoiceByMail ? "E-Mail" : "Post"}
			</CardValue>
			{invoiceByMail && invoiceEmail &&
				<CardValue label="Rechnungsversand E-Mail"
						   enableCopyToClipboard>
					{invoiceEmail}
				</CardValue>
			}
			{comment && <CardValue label="Anmerkungen">
				{nl2br(comment)}
			</CardValue>}
		</div>
		<div className="space-y-4">
			<CardValue label="Rechnungsadresse">
				{legalName || address ? (
					<div>
						{legalName && <><span className="whitespace-pre-line">{nl2br(legalName)}</span><br /></>}
						{address && <AddressDisplay address={address} />}
					</div>
				) : EMPTY_VALUE_PLACEHOLDER}
			</CardValue>
		</div>
	</div>;
};

export default BillingSettingsInvoiceRecipientDisplay;
